import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import { finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';

import { User } from '../../_models';
import { AdPreUserRequest } from '../../_models/users/AdPreUserRequest';
import { InitializePassword } from '../../_models/users/initializePassword';
import { ForgetPasswordRequest } from '../../_models/users/forgetPasswordRequest';
import { AddClientAgentRequest } from '../../_models/customers/client/AddClientAgentRequest';
import { JwtToken } from './JwtToken';
import { PreUserResponse } from "../../_models/users/PreUserResponse";
import { UserApplicationRightResponse } from "../../_models/users/UserApplicationResponse";
import { LocalStorageKey } from 'src/app/_models/constants/local-storage';
import { EnvService } from '../../_services/configurations/env.service';
import {AuthenticationResult} from '@azure/msal-browser';
import { MicrosoftAuthService } from './MicrosoftAuthService';


export interface AuthProvider {
    id: string;
    name: string;
    displayName: string;
    type: string;
    clientId?: string;
    authority?: string;
    baseId?: number;
}
interface AdAuthenticationProviderDto {
  id: number;
  name: string;
  type: string;
  configuration?: any;
  actif?: boolean;
  baseId?: number;
}
export interface MicrosoftAuthRequest {
    idToken: string;
    accessToken?: string;
}
export interface JwtTokensResponse {
  token: string;
  refreshToken: string;
  expireIn?: number;
}


@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private environment = inject(EnvService);
    private apiURL = this.environment?.readConfig()?.apiUrl;
    constructor(
        private router: Router,
        private http: HttpClient,
        private microsoftAuthService: MicrosoftAuthService) {
        const storedUser = JSON.parse(
            localStorage.getItem(LocalStorageKey.user) || 'null'
        );

        this.userSubject = new BehaviorSubject<User>(storedUser);
        this.user = this.userSubject.asObservable();

        if (storedUser?.token && this.canRefreshToken()) {
            setTimeout(() => this.startRefreshTokenTimer());
        }
    }

    public get userValue(): User {
        return this.userSubject.value;
    }

    private userSubject: BehaviorSubject<User>;
    public user: Observable<User>;

    // helper methods

    private refreshTokenTimeout;
    private refreshTokenRequest$: Observable<User> | null = null;
    private static readonly REFRESH_THRESHOLD_MS = 10 * 60 * 1000;
    private static readonly MS_BASE_ID_KEY = 'ms_base_id';


    login(login: string, password: string) {
        return this.http.post<any>(`${this.apiURL}/auth/authenticate`, { login, password }, { withCredentials: true })
            .pipe(map(user => {
                this.userSubject.next(user);
                localStorage.setItem(LocalStorageKey.user, JSON.stringify(user));
                this.startRefreshTokenTimer();
                return user;
            }));
    }
    handleInternalCallback() {
       return this.getUserInfos().pipe(map(value => {
          if(value && value.rights){
             localStorage.setItem(LocalStorageKey.userRights, JSON.stringify(value.rights));
             const user: any = {
                ...
                   this.userValue,
                rights: value.rights,
                type: value.type,
                email: value.email,
                roles: value.roles,
                firstName: value.firstName,
                lastName: value.lastName,
                username: value.login
             }
             this.userSubject.next(user);
             localStorage.setItem(LocalStorageKey.user, JSON.stringify(user));
             return user;
          }
       }));
    }

    refreshToken(refreshToken?: string, forceRefresh = false): Observable<User> {
        if (this.refreshTokenRequest$) {
            return this.refreshTokenRequest$;
        }
        let refreshRequest$: Observable<User>;
        if (this.isMicrosoftAuthentication()) {
            refreshRequest$ = from(
                this.microsoftAuthService.acquireTokenSilent(forceRefresh)
            ).pipe(
                map((result) => ({
                    ...this.userValue,
                    token: result.accessToken
                }) as User)
            );
        } else {
            const tokenToRefresh =
                refreshToken || this.userValue?.refreshToken;
            if (!tokenToRefresh) {
                return throwError(
                    () => new Error('Missing refresh token')
                );
            }
            refreshRequest$ = this.http
                .post<Partial<User>>(
                    `${this.apiURL}/auth/token/refresh`,
                    { refreshToken: tokenToRefresh },
                    { withCredentials: true }
                )
                .pipe(
                    map((response) => ({
                        ...this.userValue,
                        ...response
                    }) as User)
                );
        }
        const sharedRefresh$ = refreshRequest$.pipe(
            tap((updatedUser) => {
                localStorage.setItem(
                    LocalStorageKey.user,
                    JSON.stringify(updatedUser)
                );
                this.userSubject.next(updatedUser);
                this.startRefreshTokenTimer();
            }),
            finalize(() => {this.refreshTokenRequest$ = null;
            }),
            shareReplay(1)
        );
        this.refreshTokenRequest$ = sharedRefresh$;
        return sharedRefresh$;
    }

    private isMicrosoftAuthentication(): boolean {
        return this.userValue?.authProvider === 'MICROSOFT';
    }

    canRefreshToken(): boolean {
        return this.isMicrosoftAuthentication() ||
            !!this.userValue?.refreshToken;
    }

    isTokenCloseToExpiration(thresholdMs: number = AuthenticationService.REFRESH_THRESHOLD_MS): boolean {
        const token = this.userValue?.token;
        if (!token || !this.canRefreshToken()) {
            return false;
        }
        try {
            const helper = new JwtHelperService();
            const jwtToken = helper.decodeToken<JwtToken>(token);
            if (!jwtToken?.exp) {
                return false;
            }
            return (jwtToken.exp * 1000) - Date.now() < thresholdMs;
        } catch {
            return false;
        }
    }

    getUserInfos(): Observable<UserApplicationRightResponse> {
        const user: any = this.userValue;
        if (!user?.token) {
            return of(null);
        }
        return this.http.get<UserApplicationRightResponse>(`${this.apiURL}/auth/userinfo0`);
    }

    adPreUser(adPreUserRequest: AdPreUserRequest): Observable<any> {
        return this.http.post<any>(`${this.apiURL}/auth/register`, adPreUserRequest, { withCredentials: true });
    }

    activateCompte(id: string): Observable<PreUserResponse> {
        return this.http.get<PreUserResponse>(`${this.apiURL}/auth/activate/${id}`);
    }

    addClientAgent(addClientAgentRequest: AddClientAgentRequest): Observable<any> {
        return this.http.post<any>(`${this.apiURL}/auth/clientagent/add`, addClientAgentRequest, { withCredentials: true });
    }

    /* sendMailClientCertified(client: ClientRequest): Observable<any> {
       return this.http.post(`${this.apiURL}/auth/mailClientCertified`, client, { withCredentials: true } );
     }*/

    forgetPassword(forgetPassword: ForgetPasswordRequest): Observable<boolean> {
        return this.http.post<boolean>(`${this.apiURL}/auth/mailForgetPassword`, forgetPassword, { withCredentials: true });
    }

    initializePassword(initializePassword: InitializePassword): Observable<boolean> {
        return this.http.post<boolean>(`${this.apiURL}/auth/initializePassword`, initializePassword, { withCredentials: true });
    }


    logout() {
        this.stopRefreshTokenTimer();
        localStorage.removeItem(LocalStorageKey.user);
        localStorage.removeItem(LocalStorageKey.userRights);
        sessionStorage.clear();
        this.userSubject.next(null);
        this.router.navigate(['prospect/login'], { replaceUrl: true });
    }


    private startRefreshTokenTimer(): void {
        this.stopRefreshTokenTimer();
        if (!this.userValue?.token || !this.canRefreshToken()) {
            return;
        }
        try {
            const helper = new JwtHelperService();
            const jwtToken = helper.decodeToken<JwtToken>(this.userValue.token);
            if (!jwtToken?.exp) {
                return;
            }
            const timeout = (jwtToken.exp * 1000) - Date.now() - AuthenticationService.REFRESH_THRESHOLD_MS;
            const executeRefresh = () => {
                this.refreshToken(undefined, true).subscribe({
                    error: () => this.logout()
                });
            };
            if (timeout <= 0) {
                executeRefresh();
                return;
            }
            this.refreshTokenTimeout = setTimeout(
                executeRefresh,
                timeout
            );
        } catch {
            this.stopRefreshTokenTimer();
        }
    }

    private stopRefreshTokenTimer() {
        clearTimeout(this.refreshTokenTimeout);
    }
    getAuthProviders(): Observable<AuthProvider[]> {
        return this.http
            .get<AdAuthenticationProviderDto[]>(`${this.apiURL}/auth/authentication-provider`, { withCredentials: true })
            .pipe(
                map(list =>
                    (list || [])
                        .filter(p => p.actif !== false)
                        .map(p => {
                            let config: any = {};
                            if (p.configuration) {
                                try { config = JSON.parse(p.configuration); } catch { }
                            }
                            return {
                                id: String(p.id),
                                name: p.name,
                                displayName: config.displayName || p.name,
                                type: p.type,
                                clientId: p.configuration?.clientId,
                                authority:  p.configuration?.authority,
                                baseId: (p as any).baseId,
                            } as AuthProvider;
                        })
                )
            );
    }


    setMicrosoftBaseId(baseId: number) {
        sessionStorage.setItem(AuthenticationService.MS_BASE_ID_KEY, String(baseId));
    }

    getMicrosoftBaseId(): number | null {
        const raw = sessionStorage.getItem(AuthenticationService.MS_BASE_ID_KEY);
        const n = raw ? Number(raw) : NaN;
        return Number.isFinite(n) ? n : null;
    }

    clearMicrosoftBaseId() {
        sessionStorage.removeItem(AuthenticationService.MS_BASE_ID_KEY);
    }

    handleMicrosoftCallback(result: AuthenticationResult) {
        const preferredUsername =
            (result.idTokenClaims as any)?.preferred_username ||
            result.account?.username ||
            '';
        const displayName =
            (result.idTokenClaims as any)?.name ||
            result.account?.name ||
            '';
        const baseId =this.getMicrosoftBaseId();
        if (!baseId) {
            throw new Error('BaseId missing for Microsoft login');
        }
        const user: any = {
            token: result.accessToken,
            password: 'null',
            username: preferredUsername,
            name: displayName,
            authProvider: 'MICROSOFT',
        };

        this.userSubject.next(user);
        return this.loginWithMicrosoft(result.idToken, preferredUsername, baseId);
    }

    loginWithMicrosoft(idToken: string, preferredUsername: any, baseId: number) {
        return this.http
            .post<void>(`${this.apiURL}/v2/auth/getOrCreateUser`, null, {
                headers: new HttpHeaders({ 'X-MS-ID-TOKEN': idToken, 'X-BASE-ID': String(baseId), }),
                withCredentials: true,
            })
            .pipe(
                switchMap(() => this.getUserInfos()),
                map((value) => {
                    if (value?.rights) {
                        localStorage.setItem(LocalStorageKey.userRights, JSON.stringify(value.rights));
                    }
                    const user: any = {
                        ... this.userValue,
                        rights: value?.rights,
                        username: preferredUsername || value?.login,
                        roles: value.roles,
                       type: value.type,
                       email: value.email
                    };

                    this.userSubject.next(user);
                    localStorage.setItem(LocalStorageKey.user, JSON.stringify(user));
                    this.startRefreshTokenTimer();
                    return user;
                })
            );
    }




}
